// ** React Imports
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

// ** MUI Imports
import { AttachMoney, Logout, Person } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Divider,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  styled
} from '@mui/material'

// **Custom Component Imports
import Icon from '../../icon'
import SwitchTenant from './switch-tenant'

// **Third party Imports

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import { logout, setUserInformation } from '../../../store/slice/auth'
import {
  setSelectedWorkspace,
  setWorkspaces
} from '../../../store/slice/workspace'

// **API Imports
import { logoutUser } from '../../../axios/api/auth'
import { getCredits } from '../../../axios/api/subscription'
import { getListOfTenants } from '../../../axios/api/tenant'

// **Utils Imports
import {
  RESERVED_SUBDOMAINS,
  ROLES,
  ROLES_LABEL,
  SUBSCRIPTION_PLANS
} from '../../../utils/constant'
import {
  checkCandidateAccess,
  checkCurrentRole,
  checkDeveloperAccess,
  convertBytesToBase64,
  getInitials,
  showErrorToast
} from '../../../utils/utils'

// **Context Imports
import { SettingsContext } from '../../../context/setting'
import LearnerToggle from '../learner-toggle'

// ** Styled Components
const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.text.primary
  }
}))

// **static style
const styles = {
  px: 4,
  py: 1.75,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  '& svg': {
    mr: 1.5,
    fontSize: '1.5rem',
    color: 'text.secondary'
  }
}

const UserDropdown = () => {
  // Redux State
  const userData = useSelector((state) => state.auth.userData)
  const profile = useSelector((state) => state.auth.profile)
  const appMode = useSelector((state) => state.auth.appMode)

  //  States
  const [anchorEl, setAnchorEl] = useState(null)
  const [switchTenant, setSwitchTenant] = useState([])
  const [creditLoading, setCreditLoading] = useState(false)
  const [credits, setCredits] = useState(null)

  // Hooks
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { contestUri } = useParams()
  const location = useLocation()
  const { saveSettings } = useContext(SettingsContext)
  const currentlyActivePlan = useSelector(
    (state) => state.auth.currentlyActivePlan
  )

  // variable
  const getCreditMenuColor = useMemo(() => {
    return credits !== null && credits <= 50
      ? 'var(--error-main)'
      : 'var(--text-secondary)'
  }, [credits])

  const globalCandidatePath = useMemo(() => {
    return (
      location.pathname.endsWith('/c') ||
      location.pathname.endsWith('/c/') ||
      location.pathname.includes('/c/')
    )
  }, [location.pathname])

  const isValidSubDomainLabel = useMemo(() => {
    const hostnameParts = window.location.hostname.split('.')

    if (hostnameParts.length <= 2) {
      return false
    }

    const subDomain = hostnameParts[0].toLowerCase()
    return !RESERVED_SUBDOMAINS.includes(subDomain)
  }, [])

  const isAdminRole = useMemo(() => checkCurrentRole(ROLES.ADMIN), [])

  const isCandidate = useMemo(() => checkCandidateAccess(), [])

  const isDeveloper = useMemo(() => checkDeveloperAccess(), [])

  const isLearner = useMemo(() => checkCurrentRole(ROLES.LEARNER), [])

  const isLearnerMode = useMemo(() => appMode === 'learner', [appMode])

  const isEnterisePlan = useMemo(
    () => SUBSCRIPTION_PLANS.ENTERPRISE === currentlyActivePlan?.planName,
    [currentlyActivePlan]
  )

  const showToggle = useMemo(() => {
    return !isDeveloper && !isCandidate && !isLearner
  }, [isDeveloper, isCandidate, isLearner])

  //handlers
  const navigateToURL = useCallback(
    (url) => {
      navigate(url)
    },
    [navigate]
  )

  const fetchCredits = useCallback(async () => {
    setCreditLoading(true)
    const response = await getCredits()
    setCreditLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      setCredits(response)
    }
  }, [])

  const handleDropdownOpen = useCallback(
    (event) => {
      setAnchorEl(event.currentTarget)
      if (!isCandidate && !isDeveloper) {
        fetchCredits()
      }
    },
    [fetchCredits, isCandidate, isDeveloper]
  )

  const handleDropdownClose = useCallback(
    (url) => {
      setAnchorEl(null)
      if (url) {
        setTimeout(() => {
          navigateToURL(url)
        })
      }
    },
    [navigateToURL]
  )

  const clearSelectedWorkspace = useCallback(() => {
    dispatch(setSelectedWorkspace({ selectedWorkspace: null }))
    dispatch(setWorkspaces({ workspaces: null }))
  }, [dispatch])

  const setTenantToken = useCallback(
    (token) => {
      clearSelectedWorkspace()
      dispatch(setUserInformation({ jwt: token }))
      navigate('/')
      window.location.reload()
    },
    [dispatch, clearSelectedWorkspace, navigate]
  )

  const handleLogout = useCallback(async () => {
    if (!isDeveloper) {
      await logoutUser()
    }

    clearSelectedWorkspace()
    dispatch(logout())
    if (!isValidSubDomainLabel && !contestUri && !isDeveloper) {
      saveSettings({ tenantConfig: null })
    }
    handleDropdownClose(
      contestUri
        ? `/c/${contestUri}/login`
        : globalCandidatePath
        ? `/candidates/login`
        : '/login'
    )
  }, [
    dispatch,
    contestUri,
    isDeveloper,
    saveSettings,
    handleDropdownClose,
    globalCandidatePath,
    isValidSubDomainLabel,
    clearSelectedWorkspace
  ])

  const fetchTenants = useCallback(async () => {
    getListOfTenants().then((response) => {
      if (response?.isError) {
        setSwitchTenant([])
        showErrorToast(response)
      } else {
        setSwitchTenant(response)
      }
    })
  }, [])

  //UseEffects
  useEffect(() => {
    if (!isCandidate && !isDeveloper) {
      fetchTenants()
    }
  }, [fetchTenants, isCandidate, isDeveloper])

  return (
    <Box>
      <Box className="header-user-details-section" onClick={handleDropdownOpen}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {profile?.profilePic ? (
            <Box className="user-profile-picture-container">
              <img
                src={convertBytesToBase64(profile?.profilePic)}
                alt="User profile"
                className="user-profile-picture"
              />
            </Box>
          ) : (
            <Avatar
              alt={userData?.name || ''}
              src="/"
              sx={{ width: '2.5rem', height: '2.5rem' }}
            >
              {getInitials(userData?.name || '')}
            </Avatar>
          )}
          <Box
            sx={{
              display: 'flex',
              ml: 2,
              alignItems: 'flex-start',
              flexDirection: 'column'
            }}
          >
            <Typography
              title={userData?.name}
              sx={{
                fontWeight: 500,
                color:
                  contestUri || isCandidate
                    ? 'var(--primary-contrastText)'
                    : 'inherit'
              }}
              className="profile-user-name"
            >
              {userData?.name || ''}
            </Typography>
            {!isCandidate && (
              <Typography className="role-sub-heading">
                {ROLES_LABEL?.[userData?.roles] || ''}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        sx={{ '& .MuiMenu-paper': { minWidth: '230px', mt: 1.8 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {switchTenant?.length > 1 && !isDeveloper && !isCandidate && (
          <SwitchTenant
            data={switchTenant}
            selectedTenant={userData?.tenantId}
            setTenantToken={setTenantToken}
          />
        )}

        {showToggle && <LearnerToggle />}

        {(switchTenant?.length > 1 || showToggle) &&
          !isDeveloper &&
          !isCandidate && (
            <Divider sx={{ my: (theme) => `${theme.spacing(1)} !important` }} />
          )}

        {isAdminRole && !isLearnerMode && (
          <MenuItemStyled
            sx={{ p: 0 }}
            onClick={() => handleDropdownClose()}
            component={Link}
            to={'/subscription?active=subscription'}
          >
            <Box sx={styles}>
              <AttachMoney />
              Subscription
            </Box>
          </MenuItemStyled>
        )}

        {isAdminRole && !isLearnerMode && (
          <MenuItemStyled
            component={Link}
            onClick={() => handleDropdownClose()}
            to={'/subscription?active=purchase-history'}
            sx={{
              p: 0
            }}
          >
            <Box
              sx={{
                ...styles
              }}
            >
              <Icon icon="ix:history-list" /> Purchase History
            </Box>
          </MenuItemStyled>
        )}

        {!isCandidate && !isDeveloper && !isLearnerMode && creditLoading && (
          <Skeleton
            animation="wave"
            height={'50px'}
            width={'90%'}
            sx={{ margin: 'auto' }}
          />
        )}

        {!isCandidate &&
          !isDeveloper &&
          !isLearnerMode &&
          !creditLoading &&
          credits !== null && (
            <MenuItemStyled
              sx={{
                p: 0,
                '&:hover .MuiBox-root': {
                  color: getCreditMenuColor
                },
                pointerEvents: isAdminRole ? 'default' : 'none'
              }}
              {...(isAdminRole
                ? {
                    component: Link,
                    to: '/subscription?active=credit',
                    onClick: () => handleDropdownClose()
                  }
                : {})}
            >
              <Box
                sx={{
                  ...styles,
                  color: getCreditMenuColor
                }}
              >
                <Icon
                  icon="si:wallet-detailed-fill"
                  style={{
                    color: getCreditMenuColor
                  }}
                />
                Credits: {credits || 0}
              </Box>
            </MenuItemStyled>
          )}
        {!isCandidate && !isDeveloper && !isLearnerMode && (
          <Divider sx={{ my: (theme) => `${theme.spacing(1)} !important` }} />
        )}
        <MenuItemStyled
          sx={{ p: 0 }}
          component={Link}
          onClick={() => handleDropdownClose()}
          to={
            globalCandidatePath || contestUri
              ? '/c/profile'
              : '/settings/profile'
          }
        >
          <Box sx={styles}>
            <Person />
            Profile
          </Box>
        </MenuItemStyled>

        {isAdminRole && isEnterisePlan && !isLearnerMode && (
          <MenuItemStyled
            sx={{ p: 0 }}
            component={Link}
            to={'/settings/config'}
            onClick={() => handleDropdownClose()}
          >
            <Box sx={styles}>
              <Icon icon="icon-park-outline:setting-config" />
              Configurations
            </Box>
          </MenuItemStyled>
        )}

        <Divider sx={{ my: (theme) => `${theme.spacing(1)} !important` }} />
        <MenuItemStyled sx={{ p: 0 }} onClick={handleLogout}>
          <Box sx={styles}>
            <Logout />
            Logout
          </Box>
        </MenuItemStyled>
      </Menu>
    </Box>
  )
}

export default memo(UserDropdown)
